import type { DispatchTarget } from "@/modules/pos/components/dispatch/useFloorDispatch";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Check, Receipt } from "lucide-react";
import { Checkbox, Radio, TextInput } from "@a2z/ui";
import { useI18n, useTranslate } from "@/lib/i18n";
import { countedKey } from "@/lib/i18n/plural";
import { ApiError } from "@/lib/api/client";
import { endpoints } from "@/lib/api/endpoints";
import type {
  DeliveryTabGridEntry,
  DriverSettlementBagView,
  DriverSettlementCardView,
  DriverSettlementCommandResponse,
  DriverSettlementOutcome,
} from "@/lib/api/dispatchTypes";
import { useErrorMessage } from "@/shared/errors";
import { invalidateInvoiceMutation } from "@/shared/invalidation";
import { currencyFor, useMoney } from "@/shared/ui";
import { usePrivateToast } from "@/shared/usePrivateToast";
import { dispatchQueryKeys } from "@/modules/pos/lib/useDispatchBoard";
import { useDispatchCommand } from "@/modules/pos/lib/useDispatchCommand";
import { avatarTone, initials } from "@/modules/pos/lib/deliveryWaiting";
import { LIVE_BAG_STATES } from "@/modules/pos/lib/driverSettlementCards";
import {
  bagKey,
  cardChanges,
  cashDiff,
  carryOutcomes,
  defaultOutcomes,
  formSums,
  outcomeOf,
  parseAmount,
  prefillCounted,
  returnedBags,
  runBags,
  settleGate,
  settlementRequest,
  type CardChange,
  type Outcomes,
} from "@/modules/pos/lib/driverSettlementForm";
import { DeliverySidePanel } from "./DeliverySidePanel";
import { PaymentProofViewer } from "./PaymentProofViewer";

/** How long the armed «Tap again…» label must be on screen before the confirming tap counts (S3). */
export const ARM_DWELL_MS = 600;

/** "#12" kept left-to-right inside an Arabic list. */
const ltr = (text: string): string => `⁦${text}⁩`;

/** The live bags first, then the run in order-number order, waiting-pickup bags last (greyed). */
function orderBags(bags: readonly DriverSettlementBagView[]): DriverSettlementBagView[] {
  const rank = (bag: DriverSettlementBagView) =>
    bag.state === "ON_THE_WAY" ? 0 : bag.state === "WAITING_PICKUP" ? 2 : 1;
  return [...bags].sort((a, b) => rank(a) - rank(b) || a.orderNumber - b.orderNumber);
}

/**
 * «تسوية» — THE SETTLE PANEL (B6 · P25, plan D-42 … D-45; the design's Settle
 * aside, 400px, a bottom sheet on a phone).
 *
 * Header: initials avatar, the name, «code · Staff driver · N orders». Body:
 * «Orders on this run» — every bag with its status chip, its method and its
 * amount (struck through for a returned / voided bag); a bag still with the
 * driver keeps its dispatch controls here (Change / Follow up / End delivery /
 * the tracking share — the floor's own `floorDispatch.action`) and «Open bill»,
 * so folding the old «With a driver» table into the cards loses no action. A
 * bag with a choice (a rider's Cash / InstaPay / Paid to platform, an InstaPay
 * «Transfer not received», a cash bag «Leave owed») carries a small picker;
 * an InstaPay bag with a screenshot carries «View proof». Then the sums box,
 * «Cash handed over» (pre-filled from the driver's pending handover) with the
 * live «Matches / Short by / Over by» line, the optional pay-out
 * (`SHIFT_MANAGE`), and «Returned food received back #…» when a bag came back.
 *
 * Confirm sends ONE `POST dispatch/settlements` with a UUIDv7 command id
 * (`useDispatchCommand`: the same id only for a retry after the answer was
 * lost; a changed form is a different command). The panel sends the expected
 * cash it shows; a STALE refusal moves the panel onto the server's fresh card
 * and lists what changed, so a changed number is never confirmed blind. A
 * difference needs a second tap («Settle with difference»).
 *
 * `canSettle` false (no `DISPATCH_CASH_CONFIRM` + `INVOICE_SETTLE`): the run
 * and its dispatch controls only — no count, no confirm.
 */
export function SettleDriverPanel({
  card: liveCard,
  gone = false,
  displayName,
  bills,
  dispatchAction,
  canOpen,
  canSettle,
  canPayOut,
  canViewProof,
  onClose,
}: {
  card: DriverSettlementCardView;
  /**
   * The live list no longer has this subject (everything on it was settled or
   * paid elsewhere): the panel keeps its last card on screen, says so, and
   * confirms nothing — a refusal loop on a card the server stopped drawing.
   */
  gone?: boolean;
  displayName: string;
  bills: readonly DeliveryTabGridEntry[];
  dispatchAction: (bill: DispatchTarget) => ReactNode;
  canOpen: boolean;
  canSettle: boolean;
  canPayOut: boolean;
  canViewProof: boolean;
  onClose: () => void;
}) {
  const t = useTranslate();
  const { locale } = useI18n();
  const money = useMoney();
  const errorMessage = useErrorMessage();
  const toast = usePrivateToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { locale: routeLocale = "ar" } = useParams();
  const { send, busy } = useDispatchCommand();

  /** The server's fresh card after a STALE refusal; until then the live one. */
  const [staleCard, setStaleCard] = useState<DriverSettlementCardView | null>(null);
  const card = staleCard ?? liveCard;
  /** Set once a STALE refusal moved the panel onto a fresh card: what changed (may be empty). */
  const [changes, setChanges] = useState<CardChange[] | null>(null);
  const [outcomes, setOutcomes] = useState<Outcomes>(() => defaultOutcomes(liveCard));
  const [countedText, setCountedText] = useState(() => prefillCounted(liveCard));
  /**
   * S2: the tick is for a SET of returned bags — the set it was ticked for.
   * When the set changes (a STALE preview, or a live refetch adding a voided
   * bag) the tick no longer counts and the cashier ticks again.
   */
  const [acknowledgedFor, setAcknowledgedFor] = useState<string | null>(null);
  const returnedKey = returnedBags(card).map(bagKey).join(",");
  const returnsAcknowledged = acknowledgedFor !== null && acknowledgedFor === returnedKey;
  const [payoutOpen, setPayoutOpen] = useState(false);
  const [payoutText, setPayoutText] = useState("");
  const [payoutReason, setPayoutReason] = useState("");
  /**
   * The figures the first «Settle with difference» tap agreed to, and when the
   * armed label appeared: the confirming tap counts only on the same figures
   * and only once the armed label has been on screen for `ARM_DWELL_MS`, so a
   * double-click or a repeating Enter can never confirm a difference.
   */
  const [armed, setArmed] = useState<{ figures: string; at: number } | null>(null);
  /** One POST at a time: a second tap while one is in flight does nothing. */
  const inFlight = useRef(false);
  /** The cashier has typed in the count (the «0» prefill is then theirs). */
  const typed = useRef(false);
  const [failure, setFailure] = useState<string | null>(null);

  const payout = payoutOpen ? (parseAmount(payoutText) ?? 0) : 0;
  const sums = formSums(card, outcomes, payout);
  const gate = settleGate({
    card,
    outcomes,
    countedText,
    returnsAcknowledged,
    payoutText: payoutOpen ? payoutText : "",
    payoutReason,
  });
  const counted = parseAmount(countedText);
  const returned = returnedBags(card);
  const bags = useMemo(() => orderBags(card.bags), [card.bags]);
  const billOf = (invoiceId: string) => bills.find((bill) => bill.invoiceId === invoiceId);
  const figures = [
    sums.cashExpected,
    counted ?? "",
    payout,
    returnsAcknowledged,
    runBags(card).map((bag) => `${bagKey(bag)}=${outcomeOf(bag, outcomes)}`).join(","),
  ].join("|");
  const isArmed = armed?.figures === figures;
  const sending = busy !== null;

  // Reviewer L1 + G28: the «0» pre-fill follows the run while the cashier has
  // not typed — cash expected (an outcome changed, a fresh card) blanks it so
  // «Type the cash handed over» asks; nothing left to collect (e.g. «Paid to
  // platform») puts the «0» back. A pending handover's amount is left alone.
  useEffect(() => {
    if (!typed.current && countedText === "0" && sums.cashExpected > 0) setCountedText("");
    if (!typed.current && !card.pendingHandover && countedText === "" && sums.cashExpected === 0) setCountedText("0");
  }, [sums.cashExpected, countedText, card.pendingHandover]);

  const kindLabel =
    card.subject.kind === "DRIVER"
      ? t((m) => m.dispatch.settleKindStaff)
      : card.subject.kind === "PLATFORM"
        ? t((m) => m.dispatch.settleKindPlatform, { platform: card.aggregatorName ?? "" })
        : t((m) => m.dispatch.settleKindRider);
  // G25 c2 (P28a): every bag the panel lists — on the run, on the way and
  // waiting pickup — with a real zero form; no count once the subject is gone.
  const orderCount = card.counts.onRun + card.counts.onTheWay + card.counts.waitingPickup;
  const meta = [
    card.subject.kind === "DRIVER" && card.code ? ltr(card.code) : null,
    kindLabel,
    gone ? null : t(
      countedKey(orderCount, {
        none: (m) => m.dispatch.settleOrdersNone,
        one: (m) => m.dispatch.settleOrdersOne,
        two: (m) => m.dispatch.settleOrdersTwo,
        few: (m) => m.dispatch.settleOrdersFew,
        many: (m) => m.dispatch.settleOrders,
      }),
      { count: orderCount },
    ),
  ]
    .filter(Boolean)
    .join(" · ");

  const stillOut = (count: number) =>
    t(
      countedKey(count, {
        one: (m) => m.dispatch.settleStillOutOne,
        two: (m) => m.dispatch.settleStillOutTwo,
        few: (m) => m.dispatch.settleStillOutFew,
        many: (m) => m.dispatch.settleStillOut,
      }),
      { count },
    );

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: dispatchQueryKeys.all }),
      invalidateInvoiceMutation(queryClient),
    ]);

  /** Move onto the server's fresh card (a STALE preview), keeping the choices that still apply. */
  const adopt = (fresh: DriverSettlementCardView) => {
    setChanges(cardChanges(card, fresh));
    setOutcomes(carryOutcomes(fresh, outcomes));
    setStaleCard(fresh);
    setArmed(null);
  };

  const refusal = (error: unknown): string | null => {
    if (!(error instanceof ApiError)) return errorMessage(error);
    const body = (error.body ?? {}) as Record<string, unknown>;
    switch (error.code) {
      case "DISPATCH_SETTLEMENT_STALE": {
        const preview = body.preview as DriverSettlementCardView | undefined;
        if (preview && Array.isArray(preview.bags)) {
          adopt(preview);
          return null;
        }
        return t((m) => m.dispatch.settleErrorBagInvalid);
      }
      case "DISPATCH_SETTLEMENT_BAGS_OUT":
        // QA L2: back to the live card, so the new bag on the way and its
        // dispatch controls show.
        setStaleCard(null);
        return stillOut(typeof body.count === "number" ? body.count : card.counts.comingBack || 1);
      case "DISPATCH_SETTLEMENT_BAG_INVALID":
        setStaleCard(null);
        return t((m) => m.dispatch.settleErrorBagInvalid);
      case "DISPATCH_SETTLEMENT_OUTCOME_INVALID": {
        setStaleCard(null);
        const number = card.bags.find((bag) => bag.invoiceId === body.invoiceId)?.orderNumber;
        return typeof number === "number"
          ? t((m) => m.dispatch.settleErrorOutcome, { number })
          : t((m) => m.dispatch.settleErrorOutcomeUnknown);
      }
      case "DISPATCH_SETTLEMENT_RETURNS_UNACKNOWLEDGED":
        return t((m) => m.dispatch.settleErrorReturns);
      case "DISPATCH_SETTLEMENT_PAYOUT_EXCEEDS_CASH":
        return t((m) => m.dispatch.settleErrorPayout, {
          amount: money(typeof body.payout === "number" ? body.payout : payout),
          expected: money(typeof body.cashDue === "number" ? body.cashDue : sums.cashDue),
        });
      case "DISPATCH_COMMAND_ID_CONFLICT":
        return t((m) => m.dispatch.errorConflict);
      case "STORE_ACCESS_BUSY":
        return t((m) => m.dispatch.errorBusy);
      default:
        // RESTAURANT_SHIFT_REQUIRED, SELLER_PERMISSION_DENIED, … — the app's
        // own sentences, which branch on the reader's authority.
        return errorMessage(error);
    }
  };

  const confirm = async () => {
    if (inFlight.current || gone || gate.kind !== "ready" || counted === null) return;
    if (gate.diff.kind !== "match") {
      if (!isArmed) {
        setArmed({ figures, at: Date.now() });
        return;
      }
      // Too soon after arming: the same gesture repeating, not a decision.
      if (Date.now() - (armed?.at ?? 0) < ARM_DWELL_MS) return;
    }
    inFlight.current = true;
    try {
      setFailure(null);
      const body = settlementRequest({
        card,
        outcomes,
        counted,
        returnsAcknowledged,
        payout: payoutOpen && payout > 0 ? { amount: payout, reason: payoutReason } : null,
      });
      // The key is the whole command: a retry of the SAME form after a lost
      // answer replays the same id; anything changed is a new command.
      await send<DriverSettlementCommandResponse>(
        `settle:${JSON.stringify(body)}`,
        endpoints.restaurant.dispatchSettlements,
        body,
      );
      toast.success(t((m) => m.dispatch.settleToast, { name: displayName }));
      onClose();
    } catch (error) {
      setArmed(null);
      const text = refusal(error);
      if (text) setFailure(text);
    } finally {
      inFlight.current = false;
      await refresh();
    }
  };

  /** Cancel, the close X, the backdrop and Esc all wait while the command is in flight. */
  const close = () => {
    if (!inFlight.current && !sending) onClose();
  };

  const setOutcome = (bag: DriverSettlementBagView, outcome: DriverSettlementOutcome) => {
    setOutcomes((current) => new Map(current).set(bagKey(bag), outcome));
    setArmed(null);
  };

  const primaryLabel = gone
    ? t((m) => m.dispatch.settleGateNothing)
    : gate.kind === "onTheWay"
      ? stillOut(gate.count)
      : gate.kind === "nothingToSettle"
        ? t((m) => m.dispatch.settleGateNothing)
        : gate.kind === "tickReturns"
          ? t((m) => m.dispatch.settleGateTick)
          : gate.kind === "typeCash"
            ? t((m) => m.dispatch.settleGateType)
            : gate.kind === "payoutInvalid"
              ? t((m) => m.dispatch.settleGatePayout)
              : gate.kind === "payoutTooLarge"
                ? t((m) => m.dispatch.settleGatePayoutTooLarge)
                : gate.diff.kind === "match"
                  ? t((m) => m.dispatch.settleConfirm)
                  : isArmed
                    ? t((m) => m.dispatch.settleWithDifferenceArmed)
                    : t((m) => m.dispatch.settleWithDifference);

  const footer = (
    <>
      <button
        type="button"
        data-ui="delivery-panel-cancel"
        data-testid="settle-cancel"
        disabled={sending}
        onClick={close}
        className="inline-flex items-center"
      >
        {canSettle ? t((m) => m.common.cancel) : t((m) => m.common.close)}
      </button>
      {canSettle ? (
        <button
          type="button"
          data-ui="delivery-panel-confirm"
          data-kind={gate.kind === "ready" && gate.diff.kind !== "match" ? "difference" : "confirm"}
          data-armed={isArmed ? "true" : undefined}
          data-testid="settle-confirm"
          disabled={gone || gate.kind !== "ready" || busy !== null}
          aria-busy={busy !== null || undefined}
          onClick={() => void confirm()}
          className="inline-flex items-center"
        >
          {primaryLabel}
        </button>
      ) : null}
    </>
  );

  const methodLabel = (bag: DriverSettlementBagView, outcome: DriverSettlementOutcome | null): string => {
    switch (outcome) {
      case "SETTLE_CASH":
        return t((m) => m.dispatch.settleMethodCash);
      case "SETTLE_INSTAPAY":
        return t((m) => m.dispatch.settleMethodInstapay);
      case "SETTLE_AGGREGATOR":
        return t((m) => m.dispatch.settleMethodPlatform);
      case "LEAVE_OWED":
        return bag.doorMethod === "INSTAPAY"
          ? t((m) => m.dispatch.settleMethodNotReceived)
          : t((m) => m.dispatch.settleMethodLeaveOwed);
      case "ALREADY_PAID":
        // D-43 ex. 5: prepaid by card at the till reads «Card»; ex. 6: a bag
        // settled on its own sheet before the settlement reads «Settled
        // earlier» — never «Cash», which would read as cash still to count.
        switch (bag.settlementMethod) {
          case "CARD":
            return t((m) => m.dispatch.settleMethodCard);
          case "AGGREGATOR":
            return t((m) => m.dispatch.settleMethodPlatform);
          default:
            return t((m) => m.dispatch.settleMethodSettledEarlier);
        }
      default:
        // On the way, waiting pickup, returned, voided: how it was to be paid.
        return bag.doorMethod === "INSTAPAY"
          ? t((m) => m.dispatch.settleMethodInstapay)
          : bag.due > 0
            ? t((m) => m.dispatch.settleMethodCash)
            : t((m) => m.dispatch.settleMethodPaid);
    }
  };

  const stateLabel = (bag: DriverSettlementBagView): string => {
    switch (bag.state) {
      case "ON_THE_WAY":
        return t((m) => m.dispatch.settleStateOnTheWay);
      case "WAITING_PICKUP":
        return t((m) => m.dispatch.settleStateWaiting);
      case "DELIVERED":
        return t((m) => m.dispatch.settleStateDelivered);
      case "RETURNED":
        return t((m) => m.dispatch.settleStateReturned);
      case "VOIDED":
        return t((m) => m.dispatch.settleStateVoided);
      case "OUT_WITH_RIDER":
        return t((m) => m.dispatch.settleStateWithRider);
    }
  };

  const changeText = (change: CardChange): string => {
    switch (change.kind) {
      case "cash":
        return t((m) => m.dispatch.settleChangeCash, { amount: money(change.from), expected: money(change.to) });
      case "added":
        return t((m) => m.dispatch.settleChangeAdded, { number: change.orderNumber });
      case "removed":
        return t((m) => m.dispatch.settleChangeRemoved, { number: change.orderNumber });
      case "due":
        return t((m) => m.dispatch.settleChangeDue, {
          number: change.orderNumber,
          amount: money(change.from),
          expected: money(change.to),
        });
      case "state":
        return t((m) => m.dispatch.settleChangeState, { number: change.orderNumber });
    }
  };

  // The live line follows the count whatever else still blocks Confirm.
  const diff = counted !== null && sums.cashExpected >= 0 ? cashDiff(counted, sums.cashExpected) : null;
  const showCount = canSettle && card.counts.onRun > 0;

  return (
    <DeliverySidePanel
      leading={
        <span data-ui="settle-avatar" data-size="40" data-tone={avatarTone(subjectSeed(card))} aria-hidden="true">
          {initials(displayName)}
        </span>
      }
      title={<bdi>{displayName}</bdi>}
      subtitle={<bdi>{meta}</bdi>}
      footer={footer}
      onClose={close}
      testId="settle-panel"
    >
      {changes !== null ? (
        <div data-ui="settle-stale" role="status" data-testid="settle-stale" className="flex flex-col">
          <p className="m-0">{t((m) => m.dispatch.settleStaleLead)}</p>
          {changes.length > 0 ? (
            <ul className="m-0 flex list-none flex-col p-0">
              {changes.map((change, index) => (
                <li key={index}>{changeText(change)}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ) : null}
      {gone ? (
        <p data-ui="settle-stale" role="status" data-testid="settle-gone" className="m-0">
          {t((m) => m.dispatch.settleGone)}
        </p>
      ) : null}
      {/* c3: once the subject is gone, the refusal's «review it, then confirm»
          no longer applies — the «nothing left» line says it all. */}
      {failure && !gone ? (
        <p data-ui="settle-failure" role="alert" data-testid="settle-failure" className="m-0">
          {failure}
        </p>
      ) : null}

      {/* G25 c3 (P28a): once the subject has left the live list, its rows and
          sums are a stale snapshot — only the «nothing left» line is shown. */}
      {gone ? null : (
        <>
      <section data-ui="settle-run" className="flex flex-col">
        <h3 data-ui="settle-section-title">{t((m) => m.dispatch.settleRunTitle)}</h3>
        <ul data-ui="settle-bags" className="m-0 flex list-none flex-col p-0">
          {bags.map((bag) => {
            const outcome = bag.onRun ? outcomeOf(bag, outcomes) : null;
            const struck = bag.state === "RETURNED" || bag.state === "VOIDED";
            const live = LIVE_BAG_STATES.has(bag.state);
            const bill = live ? billOf(bag.invoiceId) : undefined;
            // A bag still OPEN (accepted before the hand-over) has no closed
            // total on the server yet: show what the floor says it owes.
            const openOwes = bag.billStatus === "OPEN" && bill ? (bill.amountDue ?? bill.runningTotal) : null;
            const amount =
              openOwes !== null
                ? openOwes
                : outcome === "ALREADY_PAID"
                  ? bag.total
                  : struck
                    ? (bag.collectedAtPickup ?? bag.due)
                    : bag.due;
            const choices = canSettle && bag.onRun && bag.allowedOutcomes.length > 1;
            const proof = canViewProof && bag.hasProof && bag.assignmentId !== null;
            // A bag paid before it left is still with the driver, but its bill
            // is SETTLED and off the floor's list: its controls (follow up,
            // «the customer got it», change driver) come from the bag itself.
            const dispatchTarget =
              bill ??
              (live && bag.assignmentId !== null
                ? {
                    invoiceId: bag.invoiceId,
                    dailySequence: bag.orderNumber,
                    status: bag.billStatus as DispatchTarget["status"],
                    channel: "DELIVERY" as const,
                    aggregatorId: bag.aggregatorId,
                    customerPhone: null,
                  }
                : undefined);
            // Every bag on the run opens its bill — a delivered one too, to see what was in it.
            const openable = canOpen;
            return (
              <li
                key={bagKey(bag)}
                data-ui="settle-bag"
                data-state={bag.state}
                data-greyed={bag.state === "WAITING_PICKUP" && !bag.onRun ? "" : undefined}
                data-testid={`settle-bag-${bag.orderNumber}`}
                className="flex flex-col"
              >
                <div data-ui="settle-bag-row" className="flex items-center">
                  <bdi dir="ltr" data-ui="settle-bag-no" className="tabular-nums">
                    #{bag.orderNumber}
                  </bdi>
                  <span data-ui="settle-chip" data-state={bag.state}>
                    {stateLabel(bag)}
                  </span>
                  <span data-ui="settle-method" data-testid={`settle-method-${bag.orderNumber}`}>
                    {openOwes !== null
                      ? openOwes > 0
                        ? t((m) => m.dispatch.settleMethodCash)
                        : t((m) => m.dispatch.settleMethodPaid)
                      : methodLabel(bag, outcome)}
                  </span>
                  <span
                    data-ui="settle-amount"
                    data-struck={struck || outcome === "LEAVE_OWED" ? "" : undefined}
                    className="tabular-nums"
                  >
                    {money(amount)}
                  </span>
                </div>
                {choices ? (
                  <div
                    role="radiogroup"
                    aria-label={t((m) => m.dispatch.settleMethodAria, { number: bag.orderNumber })}
                    data-ui="settle-choices"
                    data-testid={`settle-choices-${bag.orderNumber}`}
                    className="flex flex-wrap items-center"
                  >
                    {bag.allowedOutcomes.map((choice) => (
                      <Radio
                        key={choice}
                        size="sm"
                        name={`settle-${bagKey(bag)}`}
                        value={choice}
                        checked={outcome === choice}
                        disabled={busy !== null}
                        onChange={() => setOutcome(bag, choice)}
                        label={methodLabel(bag, choice)}
                        data-testid={`settle-choice-${bag.orderNumber}-${choice}`}
                        className="settle-choice"
                      />
                    ))}
                  </div>
                ) : null}
                {bag.changedAfterPickup && bag.collectedAtPickup !== null ? (
                  <p data-ui="settle-bag-note" data-tone="warning" className="m-0">
                    {t((m) => m.dispatch.settleChangedAfterPickup, {
                      amount: money(bag.collectedAtPickup ?? 0),
                      expected: money(bag.due),
                    })}
                  </p>
                ) : null}
                {live && bag.onRun && outcome === "SETTLE_CASH" ? (
                  <p data-ui="settle-bag-note" data-testid={`settle-delivers-${bag.orderNumber}`} className="m-0">
                    {t((m) => m.dispatch.settleDeliversOnSettle)}
                  </p>
                ) : null}
                {bag.leftOwedBefore ? (
                  <p data-ui="settle-bag-note" className="m-0">
                    {t((m) => m.dispatch.settleLeftOwedBefore)}
                  </p>
                ) : null}
                {proof || dispatchTarget || openable ? (
                  <div data-ui="settle-bag-actions" className="flex flex-wrap items-center">
                    {proof && bag.assignmentId ? (
                      <PaymentProofViewer assignmentId={bag.assignmentId} orderNumber={bag.orderNumber} />
                    ) : null}
                    {dispatchTarget ? dispatchAction(dispatchTarget) : null}
                    {openable ? (
                      <button
                        type="button"
                        data-ui="waiting-assign"
                        data-kind="open"
                        data-testid={`settle-open-${bag.orderNumber}`}
                        aria-label={t((m) => m.dispatch.pickupOpenBillAria, { sequence: bag.orderNumber })}
                        onClick={() => navigate(`/${routeLocale}/pos/invoices/${bag.invoiceId}`)}
                        className="flex items-center"
                      >
                        <Receipt aria-hidden="true" size={14} strokeWidth={2} />
                        <span>{t((m) => m.dispatch.pickupOpenBill)}</span>
                      </button>
                    ) : null}
                  </div>
                ) : null}
              </li>
            );
          })}
        </ul>
      </section>

      {card.counts.onRun > 0 ? (
        <dl data-ui="settle-sums" data-testid="settle-sums" className="m-0 flex flex-col">
          <div data-ui="settle-sum" className="flex items-center justify-between">
            <dt>{t((m) => m.dispatch.settleSumAlreadyPaid)}</dt>
            <dd className="tabular-nums">{money(sums.alreadyPaidTotal)}</dd>
          </div>
          {sums.instapayTotal > 0 ? (
            <div data-ui="settle-sum" className="flex items-center justify-between">
              <dt>{t((m) => m.dispatch.settleMethodInstapay)}</dt>
              <dd className="tabular-nums">{money(sums.instapayTotal)}</dd>
            </div>
          ) : null}
          {sums.aggregatorTotal > 0 ? (
            <div data-ui="settle-sum" className="flex items-center justify-between">
              <dt>{t((m) => m.dispatch.settleMethodPlatform)}</dt>
              <dd className="tabular-nums">{money(sums.aggregatorTotal)}</dd>
            </div>
          ) : null}
          {sums.leftOwedTotal > 0 ? (
            <div data-ui="settle-sum" className="flex items-center justify-between">
              <dt>{t((m) => m.dispatch.settleSumLeftOwed)}</dt>
              <dd className="tabular-nums">{money(sums.leftOwedTotal)}</dd>
            </div>
          ) : null}
          <div data-ui="settle-sum" className="flex items-center justify-between">
            <dt>{t((m) => m.dispatch.settleSumReturned)}</dt>
            <dd className="tabular-nums">{money(sums.returnedTotal)}</dd>
          </div>
          {payout > 0 ? (
            <div data-ui="settle-sum" className="flex items-center justify-between">
              <dt>{t((m) => m.dispatch.settleSumPayout)}</dt>
              <dd className="tabular-nums">{money(-payout)}</dd>
            </div>
          ) : null}
          <div data-ui="settle-sum" data-kind="total" className="flex items-center justify-between">
            <dt>{t((m) => m.dispatch.settleSumCashExpected)}</dt>
            <dd className="tabular-nums" data-testid="settle-cash-expected">
              {/* D-50: never a negative expected cash — a pay-out larger than
                  the cash is refused (the gate says so), so 0 is shown. */}
              {money(Math.max(0, sums.cashExpected))}
            </dd>
          </div>
        </dl>
      ) : null}

      {showCount ? (
        <div data-ui="settle-count" className="flex flex-col">
          <span data-ui="settle-label" aria-hidden="true">
            {t((m) => m.dispatch.settleCashHanded)}
          </span>
          <div data-ui="settle-input" dir="ltr" data-differs={diff && diff.kind !== "match" ? "" : undefined}>
            <TextInput
              name="settle-counted"
              inputMode="decimal"
              dir="ltr"
              autoComplete="off"
              aria-label={t((m) => m.dispatch.settleCashHanded)}
              data-testid="settle-counted"
              value={countedText}
              disabled={busy !== null}
              prefix={<span data-ui="settle-input-unit">{currencyFor(locale)}</span>}
              prefixSpace={52}
              onChange={(event) => {
                typed.current = true;
                setCountedText(event.target.value);
                setArmed(null);
              }}
            />
          </div>
          {diff ? (
            <p data-ui="settle-diff" data-kind={diff.kind} data-testid="settle-diff" role="status" className="m-0 flex items-center">
              {diff.kind === "match" ? (
                <>
                  <Check aria-hidden="true" size={14} strokeWidth={2.25} />
                  <span>{t((m) => m.dispatch.settleDiffMatch)}</span>
                </>
              ) : diff.kind === "short" ? (
                <span>{t((m) => m.dispatch.settleDiffShort, { amount: money(diff.amount) })}</span>
              ) : (
                <span>{t((m) => m.dispatch.settleDiffOver, { amount: money(diff.amount) })}</span>
              )}
            </p>
          ) : null}
        </div>
      ) : null}

      {/* D149 parity: a typed rider's or courier's petrol, paid out of the
          drawer at the settlement (SHIFT_MANAGE). An app driver is staff:
          their costs are not settled here. */}
      {showCount && canPayOut && card.subject.kind !== "DRIVER" ? (
        payoutOpen ? (
          <div data-ui="settle-payout" className="flex flex-col">
            <span data-ui="settle-label" aria-hidden="true">
              {t((m) => m.dispatch.settlePayoutAmount)}
            </span>
            <div data-ui="settle-input" dir="ltr">
              <TextInput
                name="settle-payout-amount"
                inputMode="decimal"
                dir="ltr"
                autoComplete="off"
                aria-label={t((m) => m.dispatch.settlePayoutAmount)}
                data-testid="settle-payout-amount"
                value={payoutText}
                disabled={busy !== null}
                prefix={<span data-ui="settle-input-unit">{currencyFor(locale)}</span>}
                prefixSpace={52}
                onChange={(event) => {
                  setPayoutText(event.target.value);
                  setArmed(null);
                }}
              />
            </div>
            <div data-ui="settle-payout-reason">
              <TextInput
                name="settle-payout-reason"
                aria-label={t((m) => m.dispatch.settlePayoutReason)}
                placeholder={t((m) => m.dispatch.settlePayoutReason)}
                maxLength={200}
                dir="auto"
                data-testid="settle-payout-reason"
                value={payoutReason}
                disabled={busy !== null}
                onChange={(event) => setPayoutReason(event.target.value)}
              />
            </div>
            <p data-ui="settle-bag-note" className="m-0">
              {t((m) => m.dispatch.settlePayoutHint)}
            </p>
          </div>
        ) : (
          <button
            type="button"
            data-ui="settle-payout-add"
            data-testid="settle-payout-add"
            onClick={() => setPayoutOpen(true)}
            className="self-start"
          >
            {t((m) => m.dispatch.settlePayoutAdd)}
          </button>
        )
      ) : null}

      {canSettle && returned.length > 0 ? (
        <div data-ui="settle-returns" data-checked={returnsAcknowledged ? "" : undefined}>
          <Checkbox
            data-testid="settle-returns"
            checked={returnsAcknowledged}
            disabled={busy !== null}
            onChange={(event) => {
              setAcknowledgedFor(event.target.checked ? returnedKey : null);
              setArmed(null);
            }}
            label={<span data-ui="settle-returns-title">{t((m) => m.dispatch.settleReturnsTick)}</span>}
            description={
              <span data-ui="settle-returns-numbers" className="tabular-nums">
                {returned.map((bag) => ltr(`#${bag.orderNumber}`)).join(locale === "ar" ? "، " : ", ")}
              </span>
            }
            className="w-full"
          />
        </div>
      ) : null}
        </>
      )}
    </DeliverySidePanel>
  );
}

/** The avatar colour's seed: the driver id, else the rider / platform key. */
export function subjectSeed(card: DriverSettlementCardView): string {
  return card.subject.driverSellerId ?? `${card.subject.aggregatorId ?? ""}:${card.subject.riderKey ?? ""}`;
}
